import React, { useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import TreemapModule from 'highcharts/modules/treemap';
import HeatmapModule from 'highcharts/modules/heatmap';
import Icon, { IconSizes } from '../../../components/foundations/Icon';
import css from './WidgetArtifact.css';
import ArtifactLoader from './ArtifactLoader';
import { sanitizeHighchartsConfig } from './highchartsSanitizer';
import { buildWidgetOptions } from './widgetOptions';
import { parseArtifactData } from './parseArtifactData';

// highcharts-more provides the bubble series; treemap/heatmap are separate modules.
// Without these, those chart types render blank on the chat page.
HighchartsMore(Highcharts);
TreemapModule(Highcharts);
HeatmapModule(Highcharts);

const CHART_CONTAINER_PROPS = { style: { width: '100%' } };

const WidgetArtifactInner = ({ data }) => {
    const chartRef = useRef(null);
    const containerRef = useRef(null);

    const chartData = useMemo(() => parseArtifactData(data), [data]);

    const sanitizedChartData = useMemo(() => sanitizeHighchartsConfig(chartData), [chartData]);

    const options = useMemo(() => buildWidgetOptions(sanitizedChartData), [sanitizedChartData]);

    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver(() => {
            if (chartRef.current && chartRef.current.chart) {
                chartRef.current.chart.reflow();
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    if (!sanitizedChartData) {
        return (
            <div className={css.placeholder}>
                <div className={css.placeholderHeader}>
                    <Icon name="chart-line" size={IconSizes.LARGE} />
                    <div className={css.placeholderTitle}>Widget Visualization</div>
                    <div className={css.placeholderText}>Widget configuration not available</div>
                </div>
            </div>
        );
    }

    return (
        <div ref={containerRef} className={css.chartContainer}>
            <HighchartsReact
                ref={chartRef}
                highcharts={Highcharts}
                options={options}
                containerProps={CHART_CONTAINER_PROPS}
            />
        </div>
    );
};

WidgetArtifactInner.propTypes = {
    data: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

const WidgetArtifact = ({ artifact }) => (
    <ArtifactLoader artifact={artifact}>{data => <WidgetArtifactInner data={data} />}</ArtifactLoader>
);

WidgetArtifact.propTypes = {
    artifact: PropTypes.shape({
        data: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        content_url: PropTypes.string,
    }).isRequired,
};

export default WidgetArtifact;
